/**
 * Egress Blocking Detection
 * Step 2: Functions for detecting and penalizing egress conflicts
 */

import { dwellMask } from "./bitmap";
import { isSizeCompatible, isTimeAvailable } from "./spotFiltering";
import { ParkingRequest, Placement, Spot } from "./types";

/**
 * Check if placing an aircraft at a blocking spot would trap another aircraft
 *
 * A "trap" occurs when:
 * 1. The blocking spot physically blocks the blocked spot's egress path (via ANY lane)
 * 2. The blocking aircraft departs AFTER the blocked aircraft
 *
 * Multi-lane logic: Returns true if ANY egress lane blocks the blocked spot.
 * Even if one lane is clear, if another lane blocks, this is a potential trap.
 * (The actual lane choice happens at departure time in countTrappedAircraft)
 *
 * This creates an operational problem: the blocked aircraft needs to depart
 * but cannot because the blocking aircraft is still in the way.
 *
 * @param blockingAircraft - Aircraft being placed at blocking spot
 * @param blockingSpot - Spot being considered for placement
 * @param blockedAircraft - Already-placed aircraft that might be trapped
 * @param blockedSpot - Spot where blocked aircraft is placed
 * @returns true if placement would trap the blocked aircraft
 *
 * @example
 * // A01 blocks A02's egress path
 * // Long-dwell aircraft at A01 (departs 14:00)
 * // Short-turn aircraft at A02 (departs 10:00)
 * // Result: true (A02 is trapped - can't depart at 10:00)
 */
export function wouldBlock(
  blockingAircraft: ParkingRequest,
  blockingSpot: Spot,
  blockedAircraft: ParkingRequest,
  blockedSpot: Spot
): boolean {
  // Multi-lane support: Check if ANY egress lane blocks the blocked spot
  const egressOptions = blockingSpot.egressOptions || [];

  if (egressOptions.length === 0) {
    return false; // No egress options = no blocking
  }

  // Check if ANY lane blocks this spot
  let hasBlockingRelationship = false;
  for (const option of egressOptions) {
    if (option.blocksSpotIds.includes(blockedSpot.id)) {
      hasBlockingRelationship = true;
      break;
    }
  }

  if (!hasBlockingRelationship) {
    return false; // No physical blocking relationship
  }

  // Check 2: Would the blocking aircraft depart AFTER the blocked aircraft?
  // If blocker departs after blocked, it's a trap
  // If blocker departs before or at same time, no trap
  const blockerDepartsAfter =
    blockingAircraft.departureBucket > blockedAircraft.departureBucket;

  return blockerDepartsAfter;
}

/**
 * Helper: Check if an aircraft could feasibly be placed at any of the given spots
 *
 * Checks if the aircraft is size-compatible and time-available at ANY of the spots.
 * This is used to determine if a future unplaced aircraft could end up at a spot
 * that would be blocked by the candidate placement.
 *
 * @param aircraft - Aircraft to check
 * @param spotIds - Array of spot IDs to check against
 * @param allSpots - Full list of spots
 * @returns true if aircraft could fit at ANY of the spots
 */
function couldFitAtAnySpot(
  aircraft: ParkingRequest,
  spotIds: string[],
  allSpots: Spot[]
): boolean {
  if (spotIds.length === 0) return false;

  const mask = dwellMask(aircraft, 0); // No buffer for feasibility check

  return spotIds.some((spotId) => {
    const spot = allSpots.find((s) => s.id === spotId);
    if (!spot) return false;

    // Check size compatibility and time availability
    return isSizeCompatible(spot, aircraft) && isTimeAvailable(spot, mask);
  });
}

/**
 * Count how many unplaced (future) aircraft would be trapped by a new placement
 *
 * Multi-lane logic: Uses Math.min() across lanes to find best option.
 * Returns the minimum future trap count across all available egress lanes.
 *
 * Looks ahead at aircraft that haven't been placed yet and checks if placing
 * the candidate aircraft at the candidate spot would block them from egress.
 *
 * This is critical for handling "long-dwell arrives first" scenarios where
 * an aircraft with a long dwell time arrives early but departs late, potentially
 * blocking short-turn aircraft that arrive later but depart earlier.
 *
 * @param candidateAircraft - Aircraft being considered for placement
 * @param candidateSpot - Spot being evaluated
 * @param placement - Current placement map (already-placed aircraft)
 * @param allAircraft - Full list of aircraft (to look up unplaced aircraft)
 * @param allSpots - Full list of spots (to look up spots by ID)
 * @returns Number of unplaced aircraft that would be trapped (0 = no future traps)
 *
 * @example
 * // Scenario: N650GL (08:00-20:00) arrives first, considering A01
 * // Unplaced: N822QS (08:30-10:00) will arrive later
 * // A01 has Lane 2 (blocks []) and Lane 3 (blocks [A02])
 * // Lane 2: 0 future traps, Lane 3: 1 future trap
 * // Result: min(0, 1) = 0 (aircraft will use Lane 2)
 */
export function countFutureTrappedAircraft(
  candidateAircraft: ParkingRequest,
  candidateSpot: Spot,
  placement: Placement,
  allAircraft: ParkingRequest[],
  allSpots: Spot[]
): number {
  // Multi-lane support: Calculate future trap count for EACH lane, then return min
  const egressOptions = candidateSpot.egressOptions || [];

  if (egressOptions.length === 0) {
    return 0; // No egress options = no future traps
  }

  // Multi-lane logic: Calculate future trap count for EACH lane
  const futureTrappedCountsPerLane: number[] = [];

  for (const option of egressOptions) {
    // Only count future aircraft if this lane blocks at least one spot
    if (option.blocksSpotIds.length === 0) {
      futureTrappedCountsPerLane.push(0);
      continue;
    }

    let futureTrappedCountForLane = 0;
    const placedIds = new Set(Object.keys(placement));

    for (const unplacedAircraft of allAircraft) {
      if (placedIds.has(unplacedAircraft.id)) continue;
      if (unplacedAircraft.id === candidateAircraft.id) continue;

      // Check if candidate departs after unplaced
      if (
        candidateAircraft.departureBucket > unplacedAircraft.departureBucket
      ) {
        // Only count if unplaced aircraft could actually fit at a blocked spot
        if (
          couldFitAtAnySpot(unplacedAircraft, option.blocksSpotIds, allSpots)
        ) {
          futureTrappedCountForLane++;
        }
        // Continue counting - don't break (need to count ALL future traps)
      }
    }

    futureTrappedCountsPerLane.push(futureTrappedCountForLane);
  }

  // Return the MINIMUM future trap count across all lanes
  return Math.min(...futureTrappedCountsPerLane);
}

/**
 * Count how many already-placed aircraft would be trapped by a new placement
 *
 * Multi-lane logic: Uses Math.min() across egress lanes to find the BEST option.
 * Aircraft will choose the lane with fewest traps at departure time.
 *
 * WHY MIN() NOT UNION():
 * - Union would assume aircraft uses ALL lanes simultaneously (impossible)
 * - Min() reflects reality: aircraft chooses best available lane
 * - Example: Lane 2 traps 0, Lane 3 traps 3 → min(0,3) = 0 (aircraft uses Lane 2)
 *
 * Iterates through all placed aircraft and checks if placing the candidate aircraft
 * at the candidate spot would block any of them. This is used for scoring placement
 * options - we prefer spots that trap fewer (or zero) aircraft.
 *
 * @param candidateAircraft - Aircraft being considered for placement
 * @param candidateSpot - Spot being evaluated
 * @param placement - Current placement map (already-placed aircraft)
 * @param allAircraft - Full list of aircraft (to look up aircraft by ID)
 * @param allSpots - Full list of spots (to look up spots by ID)
 * @returns Number of placed aircraft that would be trapped (0 = no traps)
 *
 * @example
 * // Current state: N822QS at A02 (departs 10:00)
 * // Evaluating: N650GL at A01 (departs 14:00)
 * // A01 has Lane 2 (blocks []) and Lane 3 (blocks [A02])
 * // Lane 2: 0 traps, Lane 3: 1 trap
 * // Result: min(0, 1) = 0 (aircraft will use Lane 2)
 */
export function countTrappedAircraft(
  candidateAircraft: ParkingRequest,
  candidateSpot: Spot,
  placement: Placement,
  allAircraft: ParkingRequest[],
  allSpots: Spot[]
): number {
  // Multi-lane support: Calculate trap count for EACH lane, then return min
  const egressOptions = candidateSpot.egressOptions || [];

  if (egressOptions.length === 0) {
    return 0; // No egress options = no blocking
  }

  // Multi-lane logic: Calculate trap count for EACH lane
  const trapCountsPerLane: number[] = [];

  for (const option of egressOptions) {
    // Only count trapped aircraft if this lane blocks at least one spot
    if (option.blocksSpotIds.length === 0) {
      trapCountsPerLane.push(0);
      continue;
    }

    let trappedCountForLane = 0;

    // Count already-placed aircraft trapped by this specific lane
    for (const aircraftId in placement) {
      const placedInfo = placement[aircraftId];
      const placedAircraft = allAircraft.find((ac) => ac.id === aircraftId);
      if (!placedAircraft) continue;

      let placedSpotId: string;
      if ("segments" in placedInfo) {
        placedSpotId = placedInfo.segments[0].spotId;
      } else {
        placedSpotId = placedInfo.spotId;
      }

      // Check if this lane blocks this specific spot
      if (!option.blocksSpotIds.includes(placedSpotId)) {
        continue; // This lane doesn't block this spot
      }

      // Check for trap/conflict
      // Snapshot mode: all aircraft have same departure time → spatial conflict
      // Temporal mode: check departure ordering → temporal trap
      const isTemporal =
        candidateAircraft.departureBucket !== placedAircraft.departureBucket;

      if (isTemporal) {
        // Temporal mode: candidate must depart after placed to trap
        if (
          candidateAircraft.departureBucket > placedAircraft.departureBucket
        ) {
          trappedCountForLane++;
        }
      } else {
        // Snapshot mode: any blocking of occupied spot is a spatial conflict
        trappedCountForLane++;
      }
    }

    // Count future aircraft trapped by this specific lane
    // NOTE: In snapshot mode, unplaced aircraft can't be "trapped" because they
    // don't have positions yet - they'll just be placed elsewhere.
    // Only count future traps in temporal mode.
    const placedIds = new Set(Object.keys(placement));
    for (const unplacedAircraft of allAircraft) {
      if (placedIds.has(unplacedAircraft.id)) continue;
      if (unplacedAircraft.id === candidateAircraft.id) continue;

      // Only count if unplaced aircraft could actually fit at a blocked spot
      if (
        !couldFitAtAnySpot(unplacedAircraft, option.blocksSpotIds, allSpots)
      ) {
        continue;
      }

      // Check for future trap/conflict
      const isTemporal =
        candidateAircraft.departureBucket !== unplacedAircraft.departureBucket;

      if (isTemporal) {
        // Temporal mode: candidate must depart after unplaced to trap
        if (
          candidateAircraft.departureBucket > unplacedAircraft.departureBucket
        ) {
          trappedCountForLane++;
        }
      }
      // Snapshot mode: Skip - unplaced aircraft aren't trapped (no position yet)
    }

    trapCountsPerLane.push(trappedCountForLane);
  }

  // Return the MINIMUM trap count across all lanes
  // WHY: Aircraft will choose the lane with fewest traps at departure
  return Math.min(...trapCountsPerLane);
}

/**
 * Find all aircraft that would be trapped by a placement
 * Returns the actual aircraft objects (not just count) for detailed reporting
 *
 * Multi-lane logic: Returns aircraft trapped by the BEST available lane (min).
 * Determines which lane has fewest traps, then returns those specific aircraft.
 *
 * WHY: Users need to see WHO is trapped, not just HOW MANY
 * USE CASE: Tooltip shows "Blocks N822QS, N697QS" instead of "Blocks 2 aircraft"
 *
 * INCLUDES: Both already-placed aircraft AND future arrivals (look-ahead)
 * EXAMPLE: N650GL at A01 traps both placed aircraft and aircraft arriving later
 *
 * @param candidateAircraft - Aircraft being considered for placement
 * @param candidateSpot - Spot being evaluated
 * @param placement - Current placement map (already-placed aircraft)
 * @param allAircraft - Full list of aircraft (to look up unplaced aircraft)
 * @param allSpots - Full list of spots (to look up spots by ID)
 * @returns Array of aircraft objects that would be trapped by this placement
 */
export function findTrappedAircraft(
  candidateAircraft: ParkingRequest,
  candidateSpot: Spot,
  placement: Placement,
  allAircraft: ParkingRequest[],
  allSpots: Spot[]
): ParkingRequest[] {
  // Multi-lane support: Find trapped aircraft for EACH lane, then return min
  const egressOptions = candidateSpot.egressOptions || [];

  if (egressOptions.length === 0) {
    return []; // No egress options = no trapped aircraft
  }

  // Multi-lane logic: Calculate trapped aircraft for EACH lane
  const trappedPerLane: ParkingRequest[][] = [];

  for (const option of egressOptions) {
    const trappedForLane: ParkingRequest[] = [];

    // PART 1: Check already-placed aircraft trapped by this lane
    for (const aircraftId in placement) {
      const placedAircraft = allAircraft.find((ac) => ac.id === aircraftId);
      if (!placedAircraft) continue;

      const placedInfo = placement[aircraftId];
      const placedSpotId =
        "segments" in placedInfo
          ? placedInfo.segments[0].spotId
          : placedInfo.spotId;

      // Check if this lane blocks this specific spot
      if (!option.blocksSpotIds.includes(placedSpotId)) {
        continue; // This lane doesn't block this spot
      }

      // Check for trap/conflict
      const isTemporal =
        candidateAircraft.departureBucket !== placedAircraft.departureBucket;

      if (isTemporal) {
        // Temporal mode: check departure ordering
        if (
          candidateAircraft.departureBucket > placedAircraft.departureBucket
        ) {
          trappedForLane.push(placedAircraft);
        }
      } else {
        // Snapshot mode: any blocking of occupied spot is a spatial conflict
        trappedForLane.push(placedAircraft);
      }
    }

    // PART 2: Check future aircraft trapped by this lane
    // NOTE: In snapshot mode, unplaced aircraft can't be "trapped" because they
    // don't have positions yet - they'll just be placed elsewhere.
    // Only count future traps in temporal mode.
    const placedIds = new Set(Object.keys(placement));
    for (const unplacedAircraft of allAircraft) {
      if (placedIds.has(unplacedAircraft.id)) continue;
      if (unplacedAircraft.id === candidateAircraft.id) continue;

      // Only count if unplaced aircraft could actually fit at a blocked spot
      if (
        !couldFitAtAnySpot(unplacedAircraft, option.blocksSpotIds, allSpots)
      ) {
        continue;
      }

      // Check for future trap/conflict
      const isTemporal =
        candidateAircraft.departureBucket !== unplacedAircraft.departureBucket;

      if (isTemporal) {
        // Temporal mode: check departure ordering
        if (
          candidateAircraft.departureBucket > unplacedAircraft.departureBucket
        ) {
          trappedForLane.push(unplacedAircraft);
        }
      }
      // Snapshot mode: Skip - unplaced aircraft aren't trapped (no position yet)
    }

    trappedPerLane.push(trappedForLane);
  }

  // Return the aircraft from the lane with FEWEST traps (min)
  // WHY: Aircraft will choose the lane with fewest traps at departure
  let minTrappedCount = Infinity;
  let bestLaneTrapped: ParkingRequest[] = [];

  for (const trappedForLane of trappedPerLane) {
    if (trappedForLane.length < minTrappedCount) {
      minTrappedCount = trappedForLane.length;
      bestLaneTrapped = trappedForLane;
    }
  }

  return bestLaneTrapped;
}

/**
 * Count trapped aircraft using precomputed masks (O(1) fast path)
 *
 * Phase 9 optimization: Uses bitmap masks instead of looping through aircraft.
 * Optionally validates against loop-based approach for debugging/verification.
 *
 * Multi-lane logic: Returns minimum trap count across all lanes (same as countTrappedAircraft).
 *
 * VALIDATION MODE: When enabled, runs both mask and loop approaches, logs mismatches.
 * Use during development to verify mask computation correctness.
 *
 * @param candidateAircraft - Aircraft being considered for placement
 * @param candidateSpot - Spot being evaluated
 * @param placement - Current placement map
 * @param allAircraft - Full list of aircraft
 * @param allSpots - Full list of spots
 * @param validate - Run loop-based validation and compare results (default: false)
 * @returns Object with trap count and optional debug info
 *
 * @example
 * // With validation enabled:
 * const result = countTrappedAircraftFast(aircraft, spot, placement, allAircraft, allSpots, true);
 * // result = { count: 2, maskDebug: { usedMasks: true, maskResult: 2, loopResult: 2, matched: true } }
 */
export function countTrappedAircraftFast(
  candidateAircraft: ParkingRequest,
  candidateSpot: Spot,
  placement: Placement,
  allAircraft: ParkingRequest[],
  allSpots: Spot[],
  validate: boolean = false
): {
  count: number;
  maskDebug?: {
    usedMasks: boolean;
    maskResult?: number;
    loopResult?: number;
    matched?: boolean;
  };
} {
  // Check if masks are available
  const hasMasks =
    candidateSpot.egressMasks && candidateSpot.egressMasks.perLane.length > 0;

  if (!hasMasks) {
    // Fallback to loop-based approach
    const loopCount = countTrappedAircraft(
      candidateAircraft,
      candidateSpot,
      placement,
      allAircraft,
      allSpots
    );
    return {
      count: loopCount,
      maskDebug: validate
        ? { usedMasks: false, loopResult: loopCount }
        : undefined,
    };
  }

  // FAST PATH: Use masks
  const trapCountsPerLane: number[] = [];

  for (const laneMask of candidateSpot.egressMasks!.perLane) {
    // Count traps for this lane using mask intersection
    let trappedCountForLane = 0;

    // Check if candidate aircraft's dwell overlaps with this lane's blocked spots' departures
    // We need to count how many aircraft this placement would trap

    // For each spot blocked by this lane, check if occupied aircraft would be trapped
    for (const blockedSpotId of laneMask.blocksSpotIds) {
      // Find if this spot is occupied
      for (const aircraftId in placement) {
        const placedInfo = placement[aircraftId];
        const placedAircraft = allAircraft.find((ac) => ac.id === aircraftId);
        if (!placedAircraft) continue;

        let placedSpotId: string;
        if ("segments" in placedInfo) {
          placedSpotId = placedInfo.segments[0].spotId;
        } else {
          placedSpotId = placedInfo.spotId;
        }

        // Check if this aircraft is at the blocked spot
        if (placedSpotId !== blockedSpotId) continue;

        // Check for trap/conflict
        const isTemporal =
          candidateAircraft.departureBucket !== placedAircraft.departureBucket;

        if (isTemporal) {
          // Temporal mode: check departure ordering
          if (
            candidateAircraft.departureBucket > placedAircraft.departureBucket
          ) {
            trappedCountForLane++;
          }
        } else {
          // Snapshot mode: any blocking of occupied spot is a spatial conflict
          trappedCountForLane++;
        }
      }
    }

    // Count future aircraft that could be trapped by this lane
    // NOTE: In snapshot mode, unplaced aircraft can't be "trapped" because they
    // don't have positions yet - they'll just be placed elsewhere.
    // Only count future traps in temporal mode.
    const placedIds = new Set(Object.keys(placement));
    for (const unplacedAircraft of allAircraft) {
      if (placedIds.has(unplacedAircraft.id)) continue;
      if (unplacedAircraft.id === candidateAircraft.id) continue;

      // Only count if unplaced aircraft could actually fit at a blocked spot
      if (
        !couldFitAtAnySpot(unplacedAircraft, laneMask.blocksSpotIds, allSpots)
      ) {
        continue;
      }

      // Check for future trap/conflict
      const isTemporal =
        candidateAircraft.departureBucket !== unplacedAircraft.departureBucket;

      if (isTemporal) {
        // Temporal mode: check departure ordering
        if (
          candidateAircraft.departureBucket > unplacedAircraft.departureBucket
        ) {
          trappedCountForLane++;
        }
      }
      // Snapshot mode: Skip - unplaced aircraft aren't trapped (no position yet)
    }

    trapCountsPerLane.push(trappedCountForLane);
  }

  const maskResult = Math.min(...trapCountsPerLane);

  // VALIDATION MODE: Compare with loop-based approach
  if (validate) {
    const loopResult = countTrappedAircraft(
      candidateAircraft,
      candidateSpot,
      placement,
      allAircraft,
      allSpots
    );

    const matched = maskResult === loopResult;

    if (!matched) {
      console.warn(
        `[Phase 9 Validation] Mismatch detected for ${candidateAircraft.tail_number} at ${candidateSpot.id}:`,
        `mask=${maskResult}, loop=${loopResult}`
      );
    }

    return {
      count: maskResult,
      maskDebug: {
        usedMasks: true,
        maskResult,
        loopResult,
        matched,
      },
    };
  }

  // Normal mode: just return mask result
  return {
    count: maskResult,
    maskDebug: { usedMasks: true, maskResult },
  };
}
